import { useState, useRef } from "react";
import "./DeliveryPartnerForm.css";

const STEPS = [
  { label: "Contact", icon: "📋" },
  { label: "Capabilities", icon: "⚡" },
  { label: "Delivery", icon: "🚀" },
];

const OPTS = {
  orgType: ["AI & Technology Consulting Firm","Software Development Company","Cloud & Infrastructure Partner","Automation Solutions Provider"],
  capabilities: ["AI & LLM Solution Delivery","Enterprise Software Development","Cloud Migration & DevOps","Managed IT Services"],
  teamSize: ["1–20 Professionals","20–50 Professionals","50–100 Professionals","100+ Professionals"],
  industries: ["Banking & Financial Services","Healthcare & Pharma","Retail & E-commerce","Manufacturing & Supply Chain"],
  techExpertise: ["OpenAI / ChatGPT APIs","Cloud Platforms (AWS, Azure, GCP)","DevOps & Automation Tools","Enterprise Application Platforms"],
  deliveryMaturity: ["Enterprise-Grade Delivery Framework","Mature Agile Delivery Process","Structured Project Management Approach","Flexible Client-Centric Delivery"],
  support: ["24/7 Dedicated Support Services","Long-Term Maintenance & Optimization","Limited Project-Based Support","No Ongoing Support Services"],
  engagementModel: ["Dedicated Delivery Partnership","Project-Based Collaboration","White-Label Service Delivery","Flexible Based on Opportunity"],
  capacity: ["₹10–50 Lakhs","₹50 Lakhs–₹1 Crore","₹1–5 Crores","₹5 Crores+"],
};

export default function DeliveryPartnerForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    fullName:"", contactNumber:"", email:"", companyName:"",
    website:"", portfolio:null,
    orgType:"", capabilities:[], teamSize:"",
    industries:[], techExpertise:[],
    deliveryMaturity:"", support:"", engagementModel:"", capacity:"",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleMulti = (k,v) => {
    const cur = form[k];
    if(k==="capabilities" && !cur.includes(v) && cur.length>=5) return;
    set(k, cur.includes(v) ? cur.filter(x=>x!==v) : [...cur,v]);
  };

  const validate = () => {
    const e = {};
    if(step===0){
      if(!form.fullName.trim()) e.fullName="Required";
      if(!form.contactNumber.trim()) e.contactNumber="Required";
      if(!form.email.trim()||!/\S+@\S+\.\S+/.test(form.email)) e.email="Valid email required";
      if(!form.website.trim()) e.website="Required";
    }
    if(step===1){
      if(!form.orgType) e.orgType="Required";
      if(!form.capabilities.length) e.capabilities="Select at least one";
      if(!form.teamSize) e.teamSize="Required";
      if(!form.industries.length) e.industries="Select at least one";
    }
    if(step===2){
      if(!form.techExpertise.length) e.techExpertise="Select at least one";
      if(!form.deliveryMaturity) e.deliveryMaturity="Required";
      if(!form.support) e.support="Required";
      if(!form.engagementModel) e.engagementModel="Required";
      if(!form.capacity) e.capacity="Required";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const next = () => { if(validate()) setStep(s=>s+1); };
  const back = () => setStep(s=>s-1);
  
  const submitToBackend = async (formData) => {
    setIsSubmitting(true);
    const submitForm = new FormData();
    
    // Append all form fields
    submitForm.append('fullName', formData.fullName);
    submitForm.append('contactNumber', formData.contactNumber);
    submitForm.append('email', formData.email);
    submitForm.append('companyName', formData.companyName);
    submitForm.append('website', formData.website);
    submitForm.append('orgType', formData.orgType);
    submitForm.append('capabilities', JSON.stringify(formData.capabilities));
    submitForm.append('teamSize', formData.teamSize);
    submitForm.append('industries', JSON.stringify(formData.industries));
    submitForm.append('techExpertise', JSON.stringify(formData.techExpertise));
    submitForm.append('deliveryMaturity', formData.deliveryMaturity);
    submitForm.append('support', formData.support);
    submitForm.append('engagementModel', formData.engagementModel);
    submitForm.append('capacity', formData.capacity);
    
    // Append file if exists
    if (formData.portfolio) {
      submitForm.append('portfolio', formData.portfolio);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/delivery-partner/submit`, {
        method: 'POST',
        body: submitForm
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
      } else {
        // Handle validation errors
        const validationErrors = {};
        if (result.errors) {
          result.errors.forEach(err => {
            validationErrors[err.param] = err.msg;
          });
        }
        setErrors(validationErrors);
        alert(result.message || 'Please fix the errors in the form');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit application. Please make sure the backend server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (e) => { 
    e.preventDefault(); 
    if (validate()) {
      submitToBackend(form);
    }
  };

  if(submitted) return (
    <div className="dpf dpf--success">
      <div className="dpf__rocket">🚀</div>
      <h2>Delivery Partnership Request Submitted</h2>
      <p>Thank you, <strong>{form.fullName}</strong>. Your delivery partner application has been received. Our alliances team will be in touch within 5 business days.</p>
      <button className="dpf__btn dpf__btn--primary" onClick={()=>{
        setSubmitted(false);
        setStep(0);
        setForm({
          fullName:"", contactNumber:"", email:"", companyName:"",
          website:"", portfolio:null,
          orgType:"", capabilities:[], teamSize:"",
          industries:[], techExpertise:[],
          deliveryMaturity:"", support:"", engagementModel:"", capacity:"",
        });
      }}>Submit Another</button>
    </div>
  );

  return (
    <div className="dpf">
      <div className="dpf__header">
        <span className="dpf__eyebrow">Delivery Network</span>
        <h1 className="dpf__title">Delivery Partner <span>Application</span></h1>
        <p className="dpf__subtitle">Join Bairees' delivery network and co-deliver transformative AI and technology solutions for enterprises.</p>
      </div>

      <div className="dpf__stepper">
        {STEPS.map((s,i)=>(
          <div key={i} className={`dpf__step-wrap ${i===step?"dpf__step-wrap--active":""} ${i<step?"dpf__step-wrap--done":""}`}>
            <div className="dpf__step-num">{i<step?"✓":`0${i+1}`}</div>
            <div className="dpf__step-label">{s.icon} {s.label}</div>
            {i<2 && <div className={`dpf__step-line ${i<step?"dpf__step-line--done":""}`} />}
          </div>
        ))}
      </div>

      <form className="dpf__form" onSubmit={submit} noValidate>
        {step===0 && (
          <div className="dpf__panel">
            <h3 className="dpf__panel-title">Contact Information</h3>
            <div className="dpf__grid dpf__grid--2">
              <F label="Full Name *" error={errors.fullName}>
                <input className={`dpf__input ${errors.fullName?"dpf__input--err":""}`} value={form.fullName} onChange={e=>set("fullName",e.target.value)} placeholder="Amit Verma" />
              </F>
              <F label="Contact Number *" error={errors.contactNumber}>
                <input className={`dpf__input ${errors.contactNumber?"dpf__input--err":""}`} value={form.contactNumber} onChange={e=>set("contactNumber",e.target.value)} placeholder="+91 98765 43210" />
              </F>
            </div>
            <F label="Professional Email *" error={errors.email}>
              <input className={`dpf__input ${errors.email?"dpf__input--err":""}`} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="amit@yourfirm.com" />
            </F>
            <div className="dpf__grid dpf__grid--2">
              <F label="Company / Organization Name">
                <input className="dpf__input" value={form.companyName} onChange={e=>set("companyName",e.target.value)} placeholder="TechBridge Solutions Pvt. Ltd." />
              </F>
              <F label="Website / Portfolio / LinkedIn *" error={errors.website}>
                <input className={`dpf__input ${errors.website?"dpf__input--err":""}`} value={form.website} onChange={e=>set("website",e.target.value)} placeholder="https://yourfirm.com" />
              </F>
            </div>
            <F label="Upload Portfolio">
              <div className="dpf__file" onClick={()=>fileRef.current.click()}>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" hidden onChange={e=>set("portfolio",e.target.files[0])} />
                <div className="dpf__file-icon">💼</div>
                <div>
                  <div className="dpf__file-main">{form.portfolio?form.portfolio.name:"Upload your portfolio or capability document"}</div>
                  <div className="dpf__file-hint">PDF, Word, or PPT supported</div>
                </div>
              </div>
            </F>
          </div>
        )}

        {step===1 && (
          <div className="dpf__panel">
            <h3 className="dpf__panel-title">Organisation & Delivery Capabilities</h3>
            <F label="Organization Type *" error={errors.orgType}>
              <div className="dpf__org-group">
                {OPTS.orgType.map(o=>(
                  <label key={o} className={`dpf__org-card ${form.orgType===o?"dpf__org-card--active":""}`}>
                    <input type="radio" name="orgType" value={o} checked={form.orgType===o} onChange={()=>set("orgType",o)} />
                    {o}
                  </label>
                ))}
              </div>
            </F>
            <F label={`Core Service Capabilities * (select up to 5 — ${form.capabilities.length}/5 selected)`} error={errors.capabilities}>
              <div className="dpf__chip-group">
                {OPTS.capabilities.map(o=>(
                  <button key={o} type="button"
                    className={`dpf__chip ${form.capabilities.includes(o)?"dpf__chip--active":""} ${!form.capabilities.includes(o)&&form.capabilities.length>=5?"dpf__chip--disabled":""}`}
                    onClick={()=>toggleMulti("capabilities",o)}>{o}</button>
                ))}
              </div>
            </F>
            <F label="Delivery Team Strength *" error={errors.teamSize}>
              <div className="dpf__team-group">
                {OPTS.teamSize.map((o,i)=>(
                  <label key={o} className={`dpf__team-card ${form.teamSize===o?"dpf__team-card--active":""}`}>
                    <input type="radio" name="teamSize" value={o} checked={form.teamSize===o} onChange={()=>set("teamSize",o)} />
                    <div className="dpf__team-icon">{["👤","👥","🏃","🏢"][i]}</div>
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            </F>
            <F label="Industry Delivery Experience * (select all that apply)" error={errors.industries}>
              <div className="dpf__chip-group">
                {OPTS.industries.map(o=>(
                  <button key={o} type="button" className={`dpf__chip ${form.industries.includes(o)?"dpf__chip--active":""}`} onClick={()=>toggleMulti("industries",o)}>{o}</button>
                ))}
              </div>
            </F>
          </div>
        )}

        {step===2 && (
          <div className="dpf__panel">
            <h3 className="dpf__panel-title">Delivery Maturity & Engagement</h3>
            <F label="Technology & Platform Expertise * (select all that apply)" error={errors.techExpertise}>
              <div className="dpf__chip-group">
                {OPTS.techExpertise.map(o=>(
                  <button key={o} type="button" className={`dpf__chip ${form.techExpertise.includes(o)?"dpf__chip--active":""}`} onClick={()=>toggleMulti("techExpertise",o)}>{o}</button>
                ))}
              </div>
            </F>
            <F label="Project Delivery Maturity Level *" error={errors.deliveryMaturity}>
              <div className="dpf__radio-group">
                {OPTS.deliveryMaturity.map(o=>(
                  <label key={o} className={`dpf__radio ${form.deliveryMaturity===o?"dpf__radio--active":""}`}>
                    <input type="radio" name="maturity" value={o} checked={form.deliveryMaturity===o} onChange={()=>set("deliveryMaturity",o)} />
                    {o}
                  </label>
                ))}
              </div>
            </F>
            <div className="dpf__grid dpf__grid--2">
              <F label="Post-Deployment Support *" error={errors.support}>
                <select className={`dpf__input dpf__select ${errors.support?"dpf__input--err":""}`} value={form.support} onChange={e=>set("support",e.target.value)}>
                  <option value="">Select support capability</option>
                  {OPTS.support.map(o=><option key={o}>{o}</option>)}
                </select>
              </F>
              <F label="Preferred Engagement Model *" error={errors.engagementModel}>
                <select className={`dpf__input dpf__select ${errors.engagementModel?"dpf__input--err":""}`} value={form.engagementModel} onChange={e=>set("engagementModel",e.target.value)}>
                  <option value="">Select engagement model</option>
                  {OPTS.engagementModel.map(o=><option key={o}>{o}</option>)}
                </select>
              </F>
            </div>
            <F label="Estimated Annual Delivery Capacity *" error={errors.capacity}>
              <div className="dpf__capacity-group">
                {OPTS.capacity.map((o,i)=>(
                  <label key={o} className={`dpf__capacity-card ${form.capacity===o?"dpf__capacity-card--active":""}`}>
                    <input type="radio" name="capacity" value={o} checked={form.capacity===o} onChange={()=>set("capacity",o)} />
                    <div className="dpf__capacity-bar">
                      <div className="dpf__capacity-fill" style={{width:`${(i+1)*25}%`}} />
                    </div>
                    {o}
                  </label>
                ))}
              </div>
            </F>
          </div>
        )}

        <div className="dpf__actions">
          {step>0 && <p className="dpf__btn dpf__btn--ghost" onClick={back}>← Back</p>}
          {step<2
            ? <p  className="dpf__btn dpf__btn--primary" onClick={next}>Continue →</p>
            : <button type="submit" className="dpf__btn dpf__btn--primary" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Application 🚀"}
              </button>
          }
        </div>
      </form>
    </div>
  );
}

function F({label,error,children}){
  return(
    <div className="dpf__field">
      <label className="dpf__label">{label}</label>
      {children}
      {error && <span className="dpf__error">{error}</span>}
    </div>
  );
}